/**
 * Sanitize HTML for safe rendering. DOMPurify requires a browser DOM;
 * during static export / SSR we skip sanitization (content loads client-side).
 */
export function sanitizeHtml(html: string): string {
  if (!html) {
    return '';
  }

  const stripUnsafeServerSide = (value: string): string => value
    .replace(/<\s*(script|iframe|object|embed|form|input|button)\b[^>]*>[\s\S]*?<\s*\/\s*\1\s*>/gi, '')
    .replace(/<\s*(script|iframe|object|embed|form|input|button)\b[^>]*\/?\s*>/gi, '')
    .replace(/\s+on[a-z]+\s*=\s*("[^"]*"|'[^']*'|[^\s>]+)/gi, '')
    .replace(/\s+(href|src)\s*=\s*(['"])\s*javascript:[\s\S]*?\2/gi, '')
    .replace(/\s+(href|src)\s*=\s*(['"])\s*data:text\/html[\s\S]*?\2/gi, '');

  if (typeof window === 'undefined') {
    return stripUnsafeServerSide(html);
  }

  // Dynamic require avoids invoking dompurify during the server bundle pass.
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const mod = require('dompurify') as {
    default?: { sanitize: (value: string, config?: Record<string, unknown>) => string };
    sanitize?: (value: string, config?: Record<string, unknown>) => string;
  };
  const purifier = mod.default ?? mod;
  if (typeof purifier.sanitize !== 'function') {
    return '';
  }

  return purifier.sanitize(html, {
    USE_PROFILES: { html: true },
    FORBID_TAGS: ['script', 'iframe', 'object', 'embed', 'form', 'input', 'button'],
    FORBID_ATTR: ['onerror', 'onclick', 'onload', 'onmouseover'],
    ALLOW_DATA_ATTR: true,
    ADD_ATTR: ['target', 'rel', 'data-legal-slug'],
  });
}
